import { Navigate } from 'react-router-dom';
import { useProvince } from '../hooks/useProvince';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { useDiplomacy } from '../hooks/useDiplomacy';
import { useUnreadCount } from '../hooks/useUnreadCount';
import { WORLD_ITEMS } from '../constants/worldNav';
import { HubCard, type HubCardStat } from '../components/shell/HubCard';
import type { SiblingItem } from '../types/nav';
import type { KingdomDiplomacyResponse } from '../types/diplomacy';

/**
 * SF-155 Component 2: ctx passed to `computeStat` — every branch guards only its own data
 * source's null/loading/error state, never a sibling's (the established contract from
 * `KingdomHub.tsx`, SF-150). World has fewer live sources than Kingdom: `diplomacy` (from
 * `useDiplomacy`, shared by the `relations`/`dossier` cards) and `unreadCount`/`unreadHasLoaded`
 * (from `useUnreadCount`) — `kingdoms`/`targetFinder`/`intel` are fixed, descriptive text with no
 * live signal (Open Question 1: Intel Hub's stat is deferred to SF-157). Unlike `KingdomHub.tsx`'s
 * context, no card here reads `province` directly (Standing/Resources cards use it via their own
 * props, not through `computeStat`), so it's not part of this context.
 */
export interface WorldHubStatContext {
  diplomacy: KingdomDiplomacyResponse | null;
  diplomacyError: string | null;
  unreadCount: number;
  unreadHasLoaded: boolean;
}

/**
 * Exported (unlike RealmHub/WarHub's page-local `computeStat`) so `WorldHub.test.tsx` can drive
 * the stat matrix with `it.each(...)` directly — mirrors `KingdomHub.tsx`'s testability precedent
 * (`react-refresh/only-export-components` suppression).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function computeStat(item: SiblingItem, ctx: WorldHubStatContext): HubCardStat {
  switch (item.key) {
    case 'kingdoms':
      // No "your status" concept — a pure browse/search entry point, unlike Buildings' barren
      // acres or Military's off/def.
      return { primary: 'Browse kingdom rosters' };
    case 'dossier': {
      // No meaningful hub-level primary summary exists without picking a specific target kingdom
      // first (useDossier(kingdomId) needs one). Secondary line reuses the Relations card's
      // already-fetched `ctx.diplomacy` at zero additional network cost — previews the same "At
      // war with:" fact DossierHeader.tsx shows on the viewer's own-kingdom default view. No
      // attention dot here — the Relations card owns that alert, avoiding a duplicate alert for
      // the same underlying fact.
      const atWar = ctx.diplomacy?.activeWar != null;
      return {
        primary: 'Scout kingdom intel',
        ...(atWar ? { secondary: 'Your kingdom is at war', secondaryTone: 'warning' as const } : {}),
      };
    }
    case 'targetFinder':
      return { primary: 'Search for attack targets' };
    case 'intel':
      // Open Question 1 (resolved, Phase 3): no staleness/freshness field exists anywhere in the
      // intel API surface today — fixed/descriptive text until SF-157 gives Intel Hub a real
      // mobile-native stat surface to reuse.
      return { primary: 'View gathered intelligence' };
    case 'recentNews': {
      // Avoids the false "All caught up" before the first poll resolves (Phase 3 Critical #1).
      if (!ctx.unreadHasLoaded) return { primary: 'Loading…' };
      const primary = ctx.unreadCount === 0 ? 'All caught up' : `${ctx.unreadCount} unread`;
      return ctx.unreadCount > 0 ? { primary, attention: 'warning' } : { primary };
    }
    case 'relations': {
      if (!ctx.diplomacy) return { primary: ctx.diplomacyError ? 'Relations unavailable' : 'Loading…' };
      const activeWar = ctx.diplomacy.activeWar;
      if (activeWar) {
        // A kingdom can only have one active war at a time (DeclareWarAction's ALREADY_AT_WAR
        // guard), so no pluralization is needed here. No `secondary` line here — the opponent
        // name is already in `primary` — so `secondaryTone` is omitted too (HubCard only renders
        // the tone alongside a `secondary` string; a lone `secondaryTone` would be dead).
        return {
          primary: `At war with ${activeWar.opponentKingdomName ?? activeWar.opponentKingdomId}`,
          attention: 'warning',
        };
      }
      const hostileCount = ctx.diplomacy.relationships.filter((r) => r.relationship === 'HOSTILE').length;
      return {
        primary: 'No active wars',
        secondary: hostileCount > 0 ? `${hostileCount} hostile relationship${hostileCount === 1 ? '' : 's'}` : undefined,
      };
    }
    case 'rankings':
      // SF-159: no live signal exists here (same class as `kingdoms`/`targetFinder` above) — the
      // page itself is a stub-data leaderboard, so a static descriptive primary line is the
      // accurate, honest hub-card summary.
      return { primary: 'See where you stand' };
    default:
      return { primary: '' };
  }
}

/**
 * SF-155 Component 2: `/world` hub screen, structurally identical to `RealmHub.tsx`/
 * `WarHub.tsx`/`KingdomHub.tsx` — a navigation surface, not a data-display page. The desktop guard
 * fires before any data check, and all six cards always render regardless of load state; only the
 * stat line degrades to a placeholder.
 *
 * Desktop hook-gating (Phase 3 decision, Critical #3): `kingdomId` is derived once, before any
 * hook call, and passed `null` on desktop — mirrors `KingdomHub.tsx` verbatim — so `useDiplomacy`'s
 * fetch effect no-ops instead of firing a call the redirect immediately discards. `useUnreadCount()`
 * needs no equivalent gating: it never fires an immediate call on mount (its first poll is
 * `setTimeout`-scheduled 30-35s out) and always mounts alongside `Sidebar` on desktop anyway, whose
 * own `useUnreadCount()` call the shared-poll dedup already collapses this one into.
 */
export function WorldHub() {
  const platform = useAppShellPlatform();
  const { province } = useProvince();
  const kingdomId = platform === 'desktop' ? null : (province?.metadata.location.kingdomId ?? null);
  const { data: diplomacy, error: diplomacyError } = useDiplomacy(kingdomId);
  const { count: unreadCount, hasLoaded: unreadHasLoaded } = useUnreadCount();

  if (platform === 'desktop') {
    return <Navigate to={WORLD_ITEMS[0].route} replace />;
  }

  return (
    <div>
      <h1 className="text-lg font-bold text-game-50 mb-4">World</h1>
      <div className="flex flex-col gap-3">
        {WORLD_ITEMS.map((item) => (
          <HubCard
            key={item.key}
            item={item}
            stat={computeStat(item, { diplomacy, diplomacyError, unreadCount, unreadHasLoaded })}
          />
        ))}
      </div>
    </div>
  );
}
